import {
  CheckIcon,
  Pin02Icon,
  SearchSmIcon,
} from '@langchain/untitled-ui-icons';
import { Input } from '@mui/joy';

import { FC, useMemo, useState } from 'react';

import Chip from '@/components/Chip';
import { DropdownMenuContent } from '@/components/Dropdown';
import { PlaygroundSettingsSchema } from '@/types/schema';
import { lowerCaseIncludes } from '@/utils/stringUtils';

const ConfigurationItem = ({
  currentConfigId,
  config,
  onClick,
  isDefault,
}: {
  currentConfigId: string | undefined;
  config: PlaygroundSettingsSchema;
  onClick: () => void;
  isDefault?: boolean;
}) => {
  const name = config.name ?? '';

  return (
    <button type="button" onClick={onClick} className="w-full">
      <div className="flex flex-row items-center justify-between rounded-md p-2 hover:bg-secondary">
        <div className="flex flex-row items-center gap-2">
          <div className="gap-1/2 flex w-full max-w-[200px] flex-col items-start">
            <span className=" truncate text-sm font-medium">{name}</span>
            {config?.description && (
              <span className="w-full truncate text-left text-xs text-tertiary">
                {config.description}
              </span>
            )}
          </div>

          {isDefault && (
            <Chip
              label="Default"
              leftIcon={
                <Pin02Icon className="h-4 w-4 text-brand-secondary [&_path]:fill-brand-green-700" />
              }
              className="border-brand bg-brand-tertiary text-brand-secondary"
            />
          )}
        </div>

        {currentConfigId === config.id && (
          <div className="my-auto shrink-0">
            <CheckIcon className="h-4 w-4 text-brand-tertiary" />
          </div>
        )}
      </div>
    </button>
  );
};

type ModelConfigurationDropdownContentProps = {
  defaultConfigId: string | undefined;
  currentConfigId: string | undefined;
  storedPlaygroundSettings: PlaygroundSettingsSchema[];
  onClick: (config: PlaygroundSettingsSchema) => void;
  handleClose: () => void;
};

const ModelConfigurationDropdownContent: FC<
  ModelConfigurationDropdownContentProps
> = ({
  defaultConfigId,
  currentConfigId,
  storedPlaygroundSettings,
  handleClose,
  onClick,
}) => {
  const [settingSearch, setSettingSearch] = useState('');
  const filteredAndSortedSettings = useMemo(() => {
    const filteredSettings = storedPlaygroundSettings.filter(
      (setting) =>
        lowerCaseIncludes(setting.name ?? '', settingSearch) ||
        lowerCaseIncludes(setting.description ?? '', settingSearch)
    );
    return filteredSettings.sort((a, b) =>
      a.id === defaultConfigId ? -1 : b.id === defaultConfigId ? 1 : 0
    );
  }, [storedPlaygroundSettings, settingSearch, defaultConfigId]);

  const renderConfigList = () => {
    if (!filteredAndSortedSettings.length) {
      return (
        <div className="py-2 text-center text-sm text-tertiary">
          No configurations found
        </div>
      );
    }
    return filteredAndSortedSettings?.map((config) => (
      <ConfigurationItem
        key={config.id}
        currentConfigId={currentConfigId}
        isDefault={config.id === defaultConfigId}
        config={config}
        onClick={() => {
          onClick(config);
          handleClose();
        }}
      />
    ));
  };

  return (
    <DropdownMenuContent
      className="max-h-[300px] w-[400px] overflow-y-auto"
      onClick={(e) => e.preventDefault()}
    >
      <Input
        placeholder="Search"
        startDecorator={<SearchSmIcon className="h-4 w-4" />}
        size="sm"
        value={settingSearch}
        onChange={(e) => setSettingSearch(e.target.value)}
      />
      {renderConfigList()}
    </DropdownMenuContent>
  );
};

export default ModelConfigurationDropdownContent;
